import * as React from "react"
import { Box, Chip, Typography } from "@mui/material"
import { format } from "date-fns"
import { ButtonIconSeeker, TableSeeker, TooltipSeeker } from "@ui-seeker/core"

import { ANNOUNCEMENT_STATES, DISPLAY_MODES, STATE_COLORS, STATE_FILTER_OPTIONS } from "@constants"
import { openEditModal } from "../../@signals"

/** What the user actually needs to know at a glance: not the `active` flag on its own, but
 *  whether the announcement is reaching anyone right now. A published announcement whose
 *  window has not opened yet, and one whose window has closed, both show `active = true`
 *  while reaching nobody.
 *
 *  The labels come from the same constant the filter's options do, so the chip and the
 *  filter cannot drift into calling the same state different things. */
export function announcementState(row, now = new Date()) {
    const state = (() => {
        if (!row.active) return ANNOUNCEMENT_STATES.RETRACTED
        const start = row.startAt ? new Date(row.startAt) : null
        const end = row.endAt ? new Date(row.endAt) : null
        if (start && now < start) return ANNOUNCEMENT_STATES.SCHEDULED
        if (end && now > end) return ANNOUNCEMENT_STATES.EXPIRED
        return ANNOUNCEMENT_STATES.LIVE
    })()

    return {
        label: STATE_FILTER_OPTIONS.find(option => option.value === state)?.label ?? state,
        color: STATE_COLORS[state],
    }
}

const _name = (list, id, key, labelKey) => list.find(item => item[key] === id)?.[labelKey] ?? `#${id}`

function AudienceCell({ row, audiences }) {
    const { customers, users } = audiences
    const targetsEveryone = !row.audienceCustomers && !row.audienceUsers

    if (targetsEveryone)
        return <Chip size="small" label="Everyone" variant="outlined" />

    const customerNames = (row.audienceCustomers ?? []).map(id => _name(customers, id, "customerID", "name"))
    const userNames = (row.audienceUsers ?? []).map(id => _name(users, id, "userID", "username"))
    const summary = [
        customerNames.length ? `${customerNames.length} customer${customerNames.length > 1 ? "s" : ""}` : null,
        userNames.length ? `${userNames.length} user${userNames.length > 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(" · ")

    return (
        <TooltipSeeker title={[...customerNames, ...userNames].join(", ")}>
            <Chip size="small" label={summary} variant="outlined" color="primary" />
        </TooltipSeeker>
    )
}

function WindowCell({ row }) {
    const start = row.startAt ? format(new Date(row.startAt), 'dd/MM/yyyy') : "—"
    const end = row.endAt ? format(new Date(row.endAt), 'dd/MM/yyyy') : "No expiry"
    return (
        <Box>
            <Typography variant="body2">{start}</Typography>
            <Typography variant="caption" color="text.secondary">→ {end}</Typography>
        </Box>
    )
}

/** Presentational on purpose: `data` and `audiences` arrive as props rather than being read
 *  from the signals here. Read inside this component, the first value that lands after the
 *  initial fetch does not re-render it — the table only repainted when its parent happened to
 *  render again. The page owns the subscription now. */
export default function AnnouncementsTable({ data, audiences, isLoading, onToggleActive, onDelete }) {
    const columns = [
        {
            name: "title",
            label: "Announcement",
            options: {
                customBodyRowRenderLite: (value, row) => {
                    const state = announcementState(row)
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip size="small" label={state.label} color={state.color} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
                        </Box>
                    )
                },
            },
        },
        {
            name: "audienceCustomers",
            label: "Audience",
            options: {
                sort: false,
                customBodyRowRenderLite: (_value, row) => <AudienceCell row={row} audiences={audiences} />,
            },
        },
        {
            name: "startAt",
            label: "Window",
            options: { customBodyRowRenderLite: (_value, row) => <WindowCell row={row} /> },
        },
        {
            name: "displayMode",
            label: "Shows",
            options: {
                customBodyRender: value => (
                    <TooltipSeeker
                        title={value === DISPLAY_MODES.ONCE
                            ? "One popup per user, ever"
                            : "On every visit while the window is open"}
                    >
                        <Typography variant="body2">
                            {value === DISPLAY_MODES.ONCE ? "Once per user" : "Always"}
                        </Typography>
                    </TooltipSeeker>
                ),
            },
        },
        {
            name: "seenCount",
            label: "Seen by",
            options: {
                customBodyRender: value => (
                    <Typography variant="body2">{value === 0 ? "—" : `${value} user${value > 1 ? "s" : ""}`}</Typography>
                ),
            },
        },
        {
            name: "announcementID",
            label: "Actions",
            options: {
                sort: false,
                filter: false,
                download: false,
                customBodyRowRenderLite: (value, row) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        <ButtonIconSeeker
                            icon="pen"
                            tooltip="Edit"
                            onClick={() => openEditModal(row)}
                        />
                        <ButtonIconSeeker
                            icon={row.active ? "eye-slash" : "eye"}
                            tooltip={row.active ? "Retract — stops showing, keeps the history" : "Publish"}
                            onClick={() => onToggleActive(value, !row.active)}
                        />
                        <ButtonIconSeeker
                            icon="trash"
                            tooltip="Delete permanently"
                            sx={{ color: "error.main" }}
                            onClick={() => onDelete(row)}
                        />
                    </Box>
                ),
            },
        },
    ]

    return (
        <TableSeeker
            data={data}
            columns={columns}
            options={{
                filter: false,
                download: false,
                viewColumns: false,
                rowsPerPage: 25,
                textLabels: {
                    body: {
                        noMatch: isLoading ? "Loading announcements…" : "No announcements yet",
                    },
                },
            }}
        />
    )
}
